import { HelpButton } from "@/components/HelpPanel";
import { createPortal } from "react-dom";
import {
  useEffect,
  useId,
  useRef,
  useState,
  type HTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type InputHTMLAttributes,
} from "react";
import { Link } from "react-router-dom";
import { Button } from "./button";
import { Badge, type BadgeTone } from "./badge";
import { Icon } from "./icon";
import { cn } from "@/lib/utils";
export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  return (
    <span
      className="avm-avatar"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        background: "var(--color-primary-soft)",
        color: "var(--color-primary)",
        fontSize: Math.round(size * 0.38),
      }}
    >
      {name
        .split(/\s+/)
        .filter(Boolean)
        .map((n) => n[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()}
    </span>
  );
}
export interface BreadcrumbItem {
  label: ReactNode;
  to?: string;
}
export function Breadcrumb({ items }: { items: BreadcrumbItem[] }) {
  return (
    <nav className="avm-crumbs" aria-label="Putanja">
      {items.map((item, i) => (
        <span className="inline-flex items-center" key={i}>
          {i > 0 && <Icon name="chevronRight" size={14} />}{" "}
          {item.to ? (
            <Link to={item.to}>{item.label}</Link>
          ) : (
            <span
              className="avm-crumbs__current"
              aria-current={i === items.length - 1 ? "page" : undefined}
            >
              {item.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}
export function PageHeader({
  title,
  meta,
  status,
  breadcrumb,
  onBack,
  actions,
  leading,
  children,
}: {
  title: ReactNode;
  meta?: ReactNode;
  status?: ReactNode;
  breadcrumb?: BreadcrumbItem[];
  onBack?: () => void;
  actions?: ReactNode;
  leading?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <header className="avm-pagehead">
      {(breadcrumb || onBack) && (
        <div className="avm-pagehead__nav">
          {onBack && (
            <Button variant="tertiary" size="sm" onClick={onBack}>
              <Icon name="arrowLeft" />
              Natrag
            </Button>
          )}
          {breadcrumb && <Breadcrumb items={breadcrumb} />}
        </div>
      )}
      <div className="avm-pagehead__main">
        {leading}
        <div className="avm-pagehead__text">
          <div className="avm-pagehead__titlerow">
            <h1 className="avm-pagehead__title">{title}</h1>
            <HelpButton />
            {status}
          </div>
          {meta && <div className="avm-pagehead__meta">{meta}</div>}
        </div>
        {actions && <div className="avm-pagehead__actions">{actions}</div>}
      </div>
      {children}
    </header>
  );
}
export function Panel({
  title,
  actions,
  footer,
  children,
  className,
  ...props
}: Omit<HTMLAttributes<HTMLElement>, "title"> & {
  title?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <section className={cn("avm-panel", className)} {...props}>
      {(title || actions) && (
        <header className="avm-panel__head">
          <h2 className="avm-panel__title">{title}</h2>
          <div className="avm-panel__actions">{actions}</div>
        </header>
      )}
      <div className="avm-panel__body is-scroll is-padded">{children}</div>
      {footer && <footer className="avm-panel__foot">{footer}</footer>}
    </section>
  );
}
export function FilterBar(props: HTMLAttributes<HTMLDivElement>) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  return <div className="avm-mobile-filters">
    <button type="button" className="avm-mobile-filters__toggle" aria-expanded={expanded} aria-controls={props.id ?? id} onClick={() => setExpanded(!expanded)}>Filtri i pretraga <span aria-hidden="true">{expanded ? "−" : "+"}</span></button>
    <div {...props} id={props.id ?? id} data-mobile-expanded={expanded} className={cn("avm-filterbar", props.className)} />
  </div>;
}
export function Table(props: HTMLAttributes<HTMLDivElement>) {
  return <div {...props} className={cn("avm-table", props.className)} />;
}
export function SelectList(props: HTMLAttributes<HTMLUListElement>) {
  return (
    <ul
      {...props}
      className={cn("avm-selectlist ui-choice-list", props.className)}
    />
  );
}
export function ActionBar(props: HTMLAttributes<HTMLDivElement>) {
  return <div {...props} className={cn("avm-actionbar", props.className)} />;
}
export function Tabs(props: HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const list = ref.current;
    const active = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !active || window.innerWidth >= 768) return;
    const item = active.getBoundingClientRect(), bounds = list.getBoundingClientRect();
    if (item.left < bounds.left || item.right > bounds.right)
      list.scrollLeft += item.left - bounds.left;
  }, [props.children]);
  return (
    <div
      {...props}
      ref={ref}
      role="tablist"
      className={cn("avm-tabs", props.className)}
    />
  );
}
export function Select(props: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...props} className={cn("avm-native-input", props.className)} />
  );
}
export function DateInput(props: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      type="date"
      className={cn("avm-native-input", props.className)}
    />
  );
}
export function Checkbox(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input {...props} type="checkbox" />;
}
export function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: ReactNode;
  required?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  children: ReactNode;
}) {
  return (
    <label className="avm-field" data-required-label>
      <span className="avm-field__label">
        {label}
        {required && <span className="avm-field__req">*</span>}
      </span>
      {children}
      {error ? (
        <span className="avm-field__error">{error}</span>
      ) : (
        hint && <span className="avm-field__hint">{hint}</span>
      )}
    </label>
  );
}
export function Confidence({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="avm-meta">Bez prijedloga</span>;
  const percent = Math.max(0, Math.min(100, Math.round(value * 100)));
  return (
    <span
      className={`avm-conf avm-conf--${percent >= 85 ? "ok" : percent >= 60 ? "warn" : "bad"}`}
      aria-label={`AI pouzdanost ${percent} %`}
    >
      <span className="avm-conf__bar" aria-hidden="true">
        <span style={{ width: `${percent}%` }} />
      </span>
      {percent} %
    </span>
  );
}
export function StatusBadge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: BadgeTone;
}) {
  return <Badge tone={tone}>{children}</Badge>;
}
export function PdfPreview({
  children,
  title = "Pregled dokumenta",
  actions,
  ...props
}: Omit<HTMLAttributes<HTMLElement>, "title"> & {
  title?: string;
  actions?: ReactNode;
}) {
  return (
    <section {...props} className={cn("avm-pdf", props.className)}>
      <header className="avm-pdf__bar">
        <div className="avm-pdf__name">
          <Icon name="file" />
          <span>{title}</span>
        </div>
        {actions}
      </header>
      <div className="avm-preview-content">{children}</div>
    </section>
  );
}
export function Toast({
  children,
  tone = "info",
}: {
  children: ReactNode;
  tone?: "ok" | "info" | "warn" | "bad";
}) {
  return (
    <div
      role={tone === "bad" ? "alert" : "status"}
      className={`avm-toast avm-toast--${tone}`}
    >
      <span className="avm-toast__icon">
        <Icon name={tone === "bad" ? "alert" : "checkCircle"} />
      </span>
      <div className="avm-toast__body">{children}</div>
    </div>
  );
}
export function Dialog({
  fullscreen = false,
  className,
  descriptionId,
  title,
  children,
  onClose,
  actions,
}: {
  fullscreen?: boolean;
  className?: string;
  descriptionId?: string;
  title: string;
  children: ReactNode;
  onClose: () => void;
  actions?: ReactNode;
}) {
  const id = useId();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const el = ref.current;
    el?.focus();
    const handle = (e: KeyboardEvent) => {
      if (className === "inbox-split-modal") e.stopPropagation();
      if (e.key === "Escape") {
        e.preventDefault();
        closeRef.current();
      }
      if (e.key === "Tab" && el) {
        const nodes = [
          ...el.querySelectorAll<HTMLElement>(
            'button:not(:disabled),a[href],input:not(:disabled),select:not(:disabled),textarea:not(:disabled),[tabindex="0"]',
          ),
        ];
        const first = nodes[0],
          last = nodes.at(-1);
        if (!first) {
          e.preventDefault();
          return;
        }
        if (
          e.shiftKey &&
          (document.activeElement === first || document.activeElement === el)
        ) {
          e.preventDefault();
          last?.focus();
        } else if (
          !e.shiftKey &&
          (document.activeElement === last || document.activeElement === el)
        ) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    el?.addEventListener("keydown", handle);
    return () => {
      el?.removeEventListener("keydown", handle);
      previous?.focus();
    };
  }, [className]);
  const content = (
    <div className={`avm-dialog-backdrop${fullscreen ? " avm-dialog-backdrop--fullscreen" : ""} ${className ?? ""}`}>
      <div
        ref={ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={id}
        aria-describedby={descriptionId}
        className={`avm-dialog${fullscreen ? " avm-dialog--fullscreen" : ""}`}
      >
        <header className="avm-dialog__head">
          <h2 id={id} className="avm-dialog__title">
            {title}
          </h2>
          <Button variant="tertiary" aria-label="Zatvori" onClick={onClose}>
            <Icon name="x" />
          </Button>
        </header>
        <div className="avm-dialog__body">{children}</div>
        {actions && <footer className="avm-dialog__foot">{actions}</footer>}
      </div>
    </div>
  );
  return fullscreen ? createPortal(content, document.body) : content;
}
